import { useL } from "@/lib/useL";
import { useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { Fare } from "./FareTable";
import { PhoneInput } from "@/components/PhoneInput";

const phoneRegex = /^[+0-9\s().-]{6,24}$/;

const quoteSchema = z.object({
  name: z.string().trim().min(1, "Name required").max(100, "Name too long (max 100)"),
  phone: z
    .string()
    .trim()
    .regex(phoneRegex, "Invalid phone number")
    .refine((v) => v.replace(/\D/g, "").length >= 8, "Invalid phone number"),
  email: z.string().trim().email("Invalid email").max(255).optional().or(z.literal("")),
  message: z.string().max(2000, "Message trop long (max 2000)").optional().or(z.literal("")),
});

interface Props {
  fare: Fare | null;
  onOpenChange: (open: boolean) => void;
}

const FareQuoteDialog = ({ fare, onOpenChange }: Props) => {
  const L = useL();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [date, setDate] = useState("");
  const [passengers, setPassengers] = useState("2");
  const [message, setMessage] = useState("");
  const [hpWebsite, setHpWebsite] = useState("");
  const [sending, setSending] = useState(false);

  const reset = () => {
    setName("");
    setPhone("");
    setEmail("");
    setDate("");
    setPassengers("2");
    setMessage("");
    setHpWebsite("");
  };

  const route = fare ? `${fare.from_location} → ${fare.to_location}` : "";

  const priceSummary = fare
    ? [
        fare.price_1_3 ? `1-3 places: ${fare.price_1_3} ${fare.currency}` : null,
        fare.price_4_7 ? `4-7 places: ${fare.price_4_7} ${fare.currency}` : null,
        fare.price_8_17 ? `8-17 places: ${fare.price_8_17} ${fare.currency}` : null,
      ]
        .filter(Boolean)
        .join(" | ")
    : "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fare) return;

    if (hpWebsite.trim() !== "") {
      toast.success(L("Request sent!", "Demande envoyée !"));
      onOpenChange(false);
      return;
    }

    const parsed = quoteSchema.safeParse({ name, phone, email, message });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Invalid data");
      return;
    }

    setSending(true);
    const bookingId = crypto.randomUUID();
    const fullMessage = [
      `Demande de devis — Trajet : ${route}`,
      `Passagers : ${passengers}`,
      priceSummary ? `Tarifs consultés : ${priceSummary}` : null,
      message ? `Message : ${message}` : null,
    ]
      .filter(Boolean)
      .join("\n");

    const { error } = await supabase.from("bookings").insert({
      id: bookingId,
      name,
      phone,
      email: email || null,
      service: "Devis transfert",
      booking_date: date || null,
      message: fullMessage,
    });

    if (error) {
      toast.error(L("Could not send, please retry or contact us on WhatsApp.", "Envoi impossible, réessayez ou contactez-nous sur WhatsApp."));
      setSending(false);
      return;
    }

    supabase.functions.invoke("send-transactional-email", {
      body: {
        templateName: "booking-admin-notification",
        idempotencyKey: `booking-admin-${bookingId}`,
        templateData: {
          name,
          phone,
          email: email || "Non renseigné",
          service: `Devis transfert — ${route}`,
          date: date || "Non précisée",
          message: fullMessage,
        },
      },
    });

    if (email) {
      supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "booking-customer-confirmation",
          recipientEmail: email,
          idempotencyKey: `booking-confirm-${bookingId}`,
          templateData: { name },
        },
      });
    }

    toast.success(L("Thank you! Your quote request has been sent.", "Merci ! Votre demande de devis a bien été envoyée."));
    setSending(false);
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={!!fare} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">{L('Request a quote', 'Demander un devis')}</DialogTitle>
          <DialogDescription className="font-body">
            {L("Route", "Trajet")} : <span className="font-semibold text-foreground">{route}</span>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            value={hpWebsite}
            onChange={(e) => setHpWebsite(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="absolute left-[-9999px] h-0 w-0 opacity-0"
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="q-name" className="font-body text-sm">{L('Full name *', 'Nom complet *')}</Label>
              <Input id="q-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="q-phone" className="font-body text-sm">{L('Phone / WhatsApp *', 'Téléphone / WhatsApp *')}</Label>
              <PhoneInput id="q-phone" value={phone} onChange={setPhone} required />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="q-email" className="font-body text-sm">{L('Email (optional)', 'Email (facultatif)')}</Label>
            <Input id="q-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={255} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="q-date" className="font-body text-sm">{L('Preferred date', 'Date souhaitée')}</Label>
              <Input id="q-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="q-pax" className="font-body text-sm">{L('Passengers', 'Passagers')}</Label>
              <Input
                id="q-pax"
                type="number"
                min={1}
                max={17}
                value={passengers}
                onChange={(e) => setPassengers(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="q-msg" className="font-body text-sm">{L('Message (luggage, flight time, stops...)', 'Message (bagages, horaire de vol, arrêts...)')}</Label>
            <Textarea id="q-msg" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} maxLength={2000} />
          </div>

          <Button type="submit" disabled={sending} className="w-full font-body font-semibold">
            <Send className="mr-2 h-4 w-4" />
            {sending ? L("Sending...", "Envoi en cours...") : L("Send my request", "Envoyer ma demande")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default FareQuoteDialog;
